import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiSend, FiBell, FiType, FiUsers, FiInfo, FiMessageSquare, FiEdit, FiList, FiClipboard } from 'react-icons/fi';
import { AuthContext } from '../contexts/AuthContext'; // To get sender info if needed
import { broadcastNotification, broadcastChangeLog } from '../utils/notificationUtils';

const BroadcastNotification = () => {
  const { currentUser, userName: adminName } = useContext(AuthContext); // Get admin info
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [targetGroup, setTargetGroup] = useState('all'); // 'all', 'trainees', 'trainers', 'admins'
  const [isChangeLog, setIsChangeLog] = useState(false); // New state for changelog mode

  // Calculate word count whenever message changes
  useEffect(() => {
    const words = message.trim().split(/\s+/).filter(Boolean); // Split by whitespace and remove empty strings
    setWordCount(words.length === 1 && words[0] === '' ? 0 : words.length);
  }, [message]);

  const handleSendNotification = async () => {
    if (!message.trim()) {
      toast.warn('Please enter a notification message.');
      return;
    }

    if (!isChangeLog && !title.trim()) {
      toast.warn('Please enter a notification title.');
      return;
    }

    setIsSending(true);
    try {
      // Clean the message before sending
      const cleanedMessage = message.trim()
                                .replace(/ +/g, ' ') // Collapse multiple spaces
                                .replace(/\n+/g, '\n'); // Collapse multiple newlines
      
      // Determine target roles based on selected group
      let targetRoles = null;
      if (targetGroup === 'trainees') {
        targetRoles = ['trainee'];
      } else if (targetGroup === 'trainers') {
        // Include both trainers and admins when sending to trainers
        targetRoles = ['trainer', 'admin'];
      } else if (targetGroup === 'admins') {
        targetRoles = ['admin'];
      }
      
      // Use our utility function based on the notification type
      let notificationCount = 0;
      
      if (isChangeLog) {
        // Send a changelog notification
        notificationCount = await broadcastChangeLog(
          cleanedMessage,
          adminName || 'Admin',
          targetRoles
        );
        
        toast.success(`Change Log sent to ${notificationCount} user(s).`);
      } else {
        // Send a regular broadcast notification
        notificationCount = await broadcastNotification(
          cleanedMessage,
          title.trim(),
          adminName || 'Admin',
          targetRoles
        );
        
        toast.success(`Notification sent to ${notificationCount} user(s).`);
      }
      
      setMessage(''); // Clear message input
      setTitle(''); // Clear title input
      setIsChangeLog(false); // Reset to regular notification

    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-5 rounded-xl shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark transition-all duration-300 max-w-md mx-auto sm:max-w-2xl md:max-w-3xl hover:shadow-xl">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-secondary-light/30 dark:border-secondary-dark/30">
        <div className="w-10 h-10 rounded-lg bg-accent-light/10 dark:bg-accent-dark/10 flex items-center justify-center text-accent-light dark:text-accent-dark">
          {isChangeLog ? (
            <FiClipboard className="w-5 h-5" />
          ) : (
            <FiMessageSquare className="w-5 h-5" />
          )}
        </div>
        <div>
          <h3 className="text-base font-bold text-text-light dark:text-text-dark">
            {isChangeLog ? 'Change Log' : 'Broadcast Notification'}
          </h3>
          <p className="text-xs text-text-light/60 dark:text-text-dark/60">
            {isChangeLog ? 'Send updates and changes to users' : 'Send a message to multiple users at once'}
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Notification type selector */}
        <div className="relative">
          <label className="flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
            <input
              type="checkbox"
              checked={isChangeLog}
              onChange={(e) => setIsChangeLog(e.target.checked)}
              className="w-4 h-4 rounded accent-accent-light dark:accent-accent-dark"
              disabled={isSending}
            />
            <div className="flex items-center gap-1.5">
              <FiClipboard className="w-3.5 h-3.5 text-accent-light dark:text-accent-dark" />
              <span>Send as Change Log</span>
              <span className="text-xs text-text-light/60 dark:text-text-dark/60">(autofills date)</span>
            </div>
          </label>
        </div>
      
        {/* Title field - Only show if not a changelog */}
        {!isChangeLog && (
          <div className="relative">
            <label htmlFor="notification-title" className="block text-xs font-medium mb-1.5 text-text-light dark:text-text-dark flex items-center gap-1.5">
              <FiEdit className="w-3.5 h-3.5 text-accent-light dark:text-accent-dark" />
              Notification Title
            </label>
            <input
              id="notification-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a clear, concise title..."
              className="w-full p-2.5 border rounded-lg bg-secondary-light/50 dark:bg-secondary-dark/50 border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark focus:border-accent-light dark:focus:border-accent-dark text-sm focus:ring-2 transition-all duration-200"
              disabled={isSending}
            />
          </div>
        )}
        
        {/* Target group selector */}
        <div className="relative">
          <label htmlFor="target-group" className="block text-xs font-medium mb-1.5 text-text-light dark:text-text-dark flex items-center gap-1.5">
            <FiUsers className="w-3.5 h-3.5 text-accent-light dark:text-accent-dark" /> 
            Recipients
          </label>
          <select
            id="target-group"
            value={targetGroup}
            onChange={(e) => setTargetGroup(e.target.value)}
            className="w-full p-2.5 border rounded-lg bg-secondary-light/50 dark:bg-secondary-dark/50 border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark focus:border-accent-light dark:focus:border-accent-dark text-sm focus:ring-2 transition-all duration-200 pr-8 appearance-none"
            disabled={isSending}
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}
          >
            <option value="all">All Active Users</option>
            <option value="trainees">Trainees Only</option>
            <option value="trainers">Trainers Only</option>
            <option value="admins">Admins Only</option>
          </select>
        </div>
        
        {/* Message textarea */}
        <div className="relative">
          <label htmlFor="notification-message" className="block text-xs font-medium mb-1.5 text-text-light dark:text-text-dark flex items-center gap-1.5">
            {isChangeLog ? (
              <FiClipboard className="w-3.5 h-3.5 text-accent-light dark:text-accent-dark" />
            ) : (
              <FiBell className="w-3.5 h-3.5 text-accent-light dark:text-accent-dark" />
            )}
            {isChangeLog ? 'Change Log Content' : 'Message Content'}
          </label>
          <textarea
            id="notification-message"
            rows="4"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={isChangeLog ? 
              "Enter change log details here...\n- Added new feature\n- Fixed issue with..." : 
              "Enter your notification message here...\n- Use dashes at line start for bullet points\n* Or asterisks work too"}
            className="w-full p-2.5 border rounded-lg bg-secondary-light/50 dark:bg-secondary-dark/50 border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark focus:border-accent-light dark:focus:border-accent-dark text-sm focus:ring-2 transition-all duration-200"
            disabled={isSending}
          />
          <div className="flex justify-between items-center text-xs text-text-light/70 dark:text-text-dark/70 mt-1.5 px-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="flex items-center gap-1.5">
                <FiInfo className="w-3 h-3" />
                Line breaks are preserved
              </span>
              <span className="flex items-center gap-1.5 ml-2">
                <FiList className="w-3 h-3" />
                Use "- " or "* " for bullet points
              </span>
            </div>
            <span className="flex items-center gap-1.5 ml-2">
               <FiType className="w-3 h-3"/> 
               {wordCount} word{wordCount !== 1 ? 's' : ''}
             </span>
          </div>
        </div>
        
        <div className="pt-2">
          <button
            onClick={handleSendNotification}
            disabled={isSending || !message.trim() || (!isChangeLog && !title.trim())}
            className="w-full py-2.5 px-4 text-sm rounded-lg bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 active:brightness-90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow disabled:shadow-none"
          >
            {isSending ? (
              <>
                <svg className="animate-spin -ml-1 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <FiSend className="w-4 h-4" />
                {isChangeLog ? 'Send Change Log' : 'Send Notification'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastNotification; 